import { Component, Inject, OnInit, Optional } from '@angular/core';
import { ModalService } from '@app/core/services/modal.service';
import { RememberService } from '@app/core/services/remember.service';
import { TranslateService } from '@app/core/services/translate.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { ShipmentGet, ShipmentKey, ShipmentOptions, ShipmentSelection, ShipmentService } from '@app/services/shipment.service';

@Component({
    selector: 'tt-shipment',
    standalone: false,
    templateUrl: './shipment.component.html',
    styleUrls: ['./shipment.component.css'],
})
export class ShipmentComponent implements OnInit {
    public page: ShipmentGet = { shipmentcontainer_keyno: 0, shipmentcontainer_name: '', shipmentcontainer_keyname: '' };
    public prodplaces: ShipmentOptions['prodplaces'] = [];
    public stockplaces: ShipmentOptions['stockplaces'] = [];
    public pods: ShipmentOptions['pods'] = [];
    public showSearch = false;
    public isNew = false;
    public saving = false;
    public creating = false;
    public cancelling = false;
    public error = '';

    private previousKey: ShipmentKey = 0;
    private originalPage = '';
    private translations = {
        shipment_cancel_new_title: '', shipment_cancel_new_message: '', shipment_cancel_new_ok: '', shipment_cancel_new_cancel: '',
        shipment_unsaved_changes_title: '', shipment_unsaved_changes_message: '', shipment_unsaved_changes_ok: '', shipment_unsaved_changes_cancel: '',
    };

    constructor(
        private shipment: ShipmentService,
        private remember: RememberService,
        private modal: ModalService,
        private translate: TranslateService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator,
    ) {
        this.navigator = useNavigatorFor(navigator, this);
    }

    public async ngOnInit(): Promise<void> {
        await Promise.all([this.loadOptions(), this.loadTranslations(), this.initializePage()]);
    }

    private async initializePage(): Promise<void> {
        try {
            const key = this.navigator?.params['shipmentcontainer_keyno'];
            if (typeof key === 'string' && key.length > 0) {
                this.setPage(Number.parseInt(key, 10));
            } else {
                this.showSearch = true;
                const settings: { variablevalue: string }[] = await this.remember.getLastStatus('shipment.settings');
                if (Array.isArray(settings) && typeof settings[0]?.variablevalue === 'string' && settings[0].variablevalue.length > 0) {
                    const selected: ShipmentSelection = JSON.parse(settings[0].variablevalue);
                    this.setPage(selected.shipmentcontainer_keyno, selected.shipmentcontainer_name, selected.shipmentcontainer_keyname);
                }
            }
            await this.loadPage();
        } catch (error) { this.showError(error); }
    }

    private async loadOptions(): Promise<void> {
        try {
            const options = await this.shipment.load();
            this.prodplaces = options.prodplaces;
            this.stockplaces = options.stockplaces;
            this.pods = options.pods;
        } catch (error) { this.showError(error); }
    }

    private async loadTranslations(): Promise<void> {
        try { this.translations = { ...this.translations, ...await this.translate.translateBatch(this.translations) }; }
        catch (error) { this.showError(error); }
    }

    private setPage(key: ShipmentKey, name?: string, keyname = ''): void {
        this.page = { shipmentcontainer_keyno: key, shipmentcontainer_name: name, shipmentcontainer_keyname: keyname };
    }

    private async loadPage(): Promise<void> {
        try {
            const page = await this.shipment.getShipment(this.page.shipmentcontainer_keyno ?? 0);
            if (!page) return;
            this.isNew = false;
            this.page = { ...page, shipmentcontainer_keyname: this.page.shipmentcontainer_keyname };
            this.originalPage = JSON.stringify(this.page);
        } catch (error) { this.showError(error); }
    }

    public searchShipments = (params?: { value?: string }): Promise<ShipmentSelection[]> => {
        return this.shipment.searchKeyname(params?.value ?? this.page.shipmentcontainer_keyname ?? '');
    };

    public async selectShipment(item: ShipmentSelection): Promise<void> {
        this.setPage(item.shipmentcontainer_keyno, item.shipmentcontainer_name, item.shipmentcontainer_keyname);
        // The legacy selector remembers only these three fields, independently of the detail request.
        void this.remember.remember('shipment.settings', JSON.stringify(this.page)).catch(error => this.showError(error));
        await this.loadPage();
    }

    public async newShipment(): Promise<void> {
        if (this.isNew || this.creating) return;
        this.creating = true;
        try {
            if (JSON.stringify(this.page) !== this.originalPage && !await this.confirm('shipment_unsaved_changes')) return;
            this.previousKey = this.page.shipmentcontainer_keyno;
            this.setPage(0, '');
            this.originalPage = JSON.stringify(this.page);
            this.isNew = true;
        } catch (error) { this.showError(error); }
        finally { this.creating = false; }
    }

    public async cancelNewShipment(): Promise<void> {
        if (this.cancelling) return;
        this.cancelling = true;
        try {
            if (!await this.confirm('shipment_cancel_new')) return;
            this.isNew = false;
            this.setPage(this.previousKey);
            await this.loadPage();
        } catch (error) { this.showError(error); }
        finally { this.cancelling = false; }
    }

    public async save(): Promise<void> {
        if (this.saving) return;
        this.saving = true;
        try {
            const saved = await this.shipment.save(this.page);
            if (this.isNew) {
                this.isNew = false;
                this.setPage(saved.shipmentcontainer_keyno);
            }
            // Saving unlocks as soon as the save resolves; the legacy detail reload continues independently.
            void this.loadPage();
        } catch (error) { this.showError(error); }
        finally { this.saving = false; }
    }

    private confirm(prefix: 'shipment_unsaved_changes' | 'shipment_cancel_new'): Promise<boolean> {
        return this.modal.openConfirmDialog({
            type: 'warning', title: this.translations[`${prefix}_title`], message: this.translations[`${prefix}_message`],
            ok: this.translations[`${prefix}_ok`], cancel: this.translations[`${prefix}_cancel`],
        });
    }

    private showError(error: unknown): void {
        this.error = error instanceof Error ? error.message : String(error);
    }
}
